import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ArrowUp, ArrowDown, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const inputCls = "w-full bg-transparent border border-ink/15 rounded-md py-2 px-3 text-ink text-sm focus:outline-none focus:border-trailride transition-colors";
const empty = { name: "", title: "", bio: "", image_url: "", linkedin_url: "", order: 0 };

function BoardEditor({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, title: form.title, bio: form.bio,
      image_url: form.image_url || "", linkedin_url: form.linkedin_url || "",
      order: Number(form.order) || 0,
    };
    try {
      if (editing) await api.put(`/admin/board-members/${initial.id}`, payload);
      else await api.post("/admin/board-members", payload);
      toast.success(editing ? "Board member updated" : "Board member added");
      onSaved();
      onClose();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/40 flex items-start justify-center overflow-y-auto p-4" data-testid="board-editor">
      <div className="bg-paper w-full max-w-lg rounded-lg my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="font-serif text-xl font-bold text-ink">{editing ? "Edit board member" : "New board member"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="board-editor-close" className="text-ink/50 hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-xs text-ink/60">Name</label>
              <input required value={form.name} onChange={set("name")} data-testid="board-name" className={inputCls} />
            </div>
            <div>
              <label className="font-sans text-xs text-ink/60">Title / role</label>
              <input required value={form.title} onChange={set("title")} data-testid="board-title" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">Mini bio</label>
            <textarea rows={3} value={form.bio} onChange={set("bio")} data-testid="board-bio" className={inputCls} />
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">Photo URL (optional)</label>
            <input value={form.image_url} onChange={set("image_url")} placeholder="https://…" data-testid="board-image" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-xs text-ink/60">LinkedIn URL</label>
              <input value={form.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/…" data-testid="board-linkedin" className={inputCls} />
            </div>
            <div>
              <label className="font-sans text-xs text-ink/60">Order</label>
              <input type="number" value={form.order} onChange={set("order")} data-testid="board-order" className={inputCls} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-ink/20 font-sans text-sm text-ink hover:bg-ink/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="board-save" className="px-6 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function BoardTab() {
  const [members, setMembers] = useState([]);
  const [editor, setEditor] = useState(null);
  const load = useCallback(() => api.get("/admin/board-members").then((r) => setMembers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const reorder = async (m, dir) => {
    try { await api.put(`/admin/board-members/${m.id}`, { ...m, order: (m.order || 0) + dir }); load(); } catch {}
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await api.delete(`/admin/board-members/${m.id}`); toast.success("Removed"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-board-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold text-ink">Executive board</h2>
          <p className="font-sans text-sm text-ink/55 mt-1">Add board members, mini bios, and LinkedIn links (shown on the About page).</p>
        </div>
        <button onClick={() => setEditor({})} data-testid="admin-new-board" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark">
          <Plus size={16} /> Add member
        </button>
      </div>

      <div className="border border-ink/10 rounded-lg divide-y divide-ink/10">
        {members.length === 0 && <p className="p-6 font-sans text-ink/50">No board members yet.</p>}
        {members.map((m) => (
          <div key={m.id} className="p-4 flex items-center gap-4" data-testid={`admin-board-row-${m.id}`}>
            <div className="w-10 h-10 rounded-full bg-ink text-paper flex items-center justify-center font-serif text-sm shrink-0 overflow-hidden">
              {m.image_url ? <img src={m.image_url} alt="" className="w-full h-full object-cover grayscale-img" /> : m.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-ink truncate">{m.name} {m.linkedin_url && <Linkedin size={13} className="inline text-ink/40" />}</p>
              <p className="font-sans text-sm text-trailride">{m.title}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => reorder(m, -1)} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><ArrowUp size={15} /></button>
              <button onClick={() => reorder(m, 1)} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><ArrowDown size={15} /></button>
              <button onClick={() => setEditor(m)} data-testid={`admin-edit-board-${m.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Pencil size={16} /></button>
              <button onClick={() => remove(m)} data-testid={`admin-delete-board-${m.id}`} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editor && <BoardEditor initial={editor.id ? editor : null} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
